import React, { useRef } from 'react';
import { useSticker } from '../../context/StickerContext';
import { Undo, Redo, Upload, Image as ImageIcon } from 'lucide-react';
import { PrimaryButton } from '../ui/Buttons';
interface EditorCanvasProps {
  zoom?: number;
  showInnerCutline?: boolean;
  showOuterCutline?: boolean;
  previewCutlines?: boolean;
  activeCutline?: 'inner' | 'outer';
  onCutlineClick?: (cutline: 'inner' | 'outer') => void;
}
export function EditorCanvas({
  zoom = 50,
  showInnerCutline = true,
  showOuterCutline = true,
  previewCutlines = true,
  activeCutline = 'outer',
  onCutlineClick
}: EditorCanvasProps) {
  const {
    uploadedFile,
    setUploadedFile,
    setUploadState
  } = useSticker();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setUploadState('loading');
      setTimeout(() => {
        setUploadedFile({
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
          thumbnail: URL.createObjectURL(file)
        });
        setUploadState('loaded');
      }, 1000);
    }
  };
  const scale = zoom / 100;
  return <div className="flex-1 bg-gray-100 relative overflow-auto flex items-center justify-center p-12" data-id="element-168">
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
      backgroundSize: '20px 20px'
    }} data-id="element-169" />
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-sm border border-[#D9D9D9] p-1 flex gap-1 z-20" data-id="element-170">
        <button className="p-2 hover:bg-gray-50 rounded text-[#5F5F5F]" data-id="element-171">
          <Undo size={18} data-id="element-172" />
        </button>
        <button className="p-2 hover:bg-gray-50 rounded text-[#5F5F5F]" data-id="element-173">
          <Redo size={18} data-id="element-174" />
        </button>
      </div>
      <div className="bg-white shadow-lg rounded-sm relative flex items-center justify-center z-10 transition-transform duration-200 origin-center" style={{
      width: 600,
      height: 600,
      transform: `scale(${scale})`
    }} data-id="element-175">
        {previewCutlines && uploadedFile && <>
            {showOuterCutline && <div className={`absolute inset-4 border-[3px] border-dashed border-[#FF6B35] rounded-3xl pointer-events-none z-20 transition-opacity duration-200 ${activeCutline === 'outer' ? 'opacity-100' : 'opacity-40'}`} data-id="element-176" />}
            {showInnerCutline && <div className={`absolute inset-12 border-2 border-dotted border-[#008696] rounded-2xl pointer-events-none z-20 transition-opacity duration-200 ${activeCutline === 'inner' ? 'opacity-100' : 'opacity-40'}`} data-id="element-177" />}
          </>}
        {uploadedFile ? <div className="w-3/4 h-3/4 relative flex items-center justify-center" data-id="element-178">
            <div className="absolute inset-0 opacity-10 rounded-xl" style={{
          backgroundImage: 'linear-gradient(45deg, #000 25%, transparent 25%), linear-gradient(-45deg, #000 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #000 75%), linear-gradient(-45deg, transparent 75%, #000 75%)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
        }} data-id="element-179" />
            {uploadedFile.thumbnail ? <img src={uploadedFile.thumbnail} alt="Sticker" className="w-full h-full object-contain relative z-10" data-id="element-180" /> : <div className="w-full h-full flex items-center justify-center bg-gray-50 text-gray-400" data-id="element-181">
                <ImageIcon size={64} data-id="element-182" />
              </div>}
          </div> : <div className="flex flex-col items-center justify-center text-center p-8" data-id="element-183">
            <div className="w-24 h-24 border-2 border-dashed border-[#D9D9D9] rounded-full flex items-center justify-center mb-4 bg-gray-50" data-id="element-184">
              <Upload className="text-gray-300" size={32} data-id="element-185" />
            </div>
            <h3 className="text-lg font-semibold text-[#111111] mb-2" data-id="element-186">
              Upload a design to get started
            </h3>
            <p className="text-sm text-[#5F5F5F] mb-6 max-w-xs" data-id="element-187">
              Drag and drop your artwork here or choose a file from your
              computer.
            </p>
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept=".png,.jpg,.jpeg,.svg,.pdf,.ai,.eps" data-id="element-188" />
            <PrimaryButton className="!py-2 !px-4 text-sm" onClick={() => fileInputRef.current?.click()} data-id="element-189">
              Choose File
            </PrimaryButton>
          </div>}
      </div>

      {/* Legend Pill */}
      {uploadedFile && previewCutlines && <div className="absolute bottom-6 right-6 bg-white rounded-full shadow-md border border-[#D9D9D9] px-4 py-2 flex items-center gap-4 z-30" data-id="element-190">
          <button onClick={() => onCutlineClick?.('outer')} className={`flex items-center gap-2 text-sm transition-colors ${activeCutline === 'outer' ? 'font-bold text-[#111111]' : 'font-medium text-[#5F5F5F] hover:text-[#111111]'}`} data-id="element-191">
            <span className="w-3 h-3 rounded-full bg-[#FF6B35]" data-id="element-192" />
            Outer Cutline
          </button>
          <div className="w-px h-4 bg-[#D9D9D9]" data-id="element-193" />
          <button onClick={() => onCutlineClick?.('inner')} className={`flex items-center gap-2 text-sm transition-colors ${activeCutline === 'inner' ? 'font-bold text-[#111111]' : 'font-medium text-[#5F5F5F] hover:text-[#111111]'}`} data-id="element-194">
            <span className="w-3 h-3 rounded-full bg-[#008696]" data-id="element-195" />
            Inner Cutline
          </button>
        </div>}
    </div>;
}